import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { VersionRange } from '../../core/version-range.js';
import { LocalizedString } from '../localized-string.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export enum SeatReservationItemStatus {
  Live = 'LIVE',
  Sold = 'SOLD',
  Cancelled = 'CANCELLED',
}

export class SeatReservationItem extends DataModel {
  static readonly TYPE_ID: string = '686c5c44f13244878a29d4a85466a9d3';
  static readonly SCHEMA_VERSION: number = 4;

  constructor(
    public readonly seatName: LocalizedString,
    public readonly seatId: number,
    public readonly ticketTemplateId: string | null,
    public readonly actionBy: string | null,
    public readonly actionDate: dayjs.Dayjs | null,
    public readonly status: SeatReservationItemStatus = SeatReservationItemStatus.Live,
  ) {
    super();
  }

  get $typeId() {
    return SeatReservationItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, SeatReservationItem.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): SeatReservationItem {
    return new SeatReservationItem(
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'seatName') as SerializedData),
        MapUtils.get<number>(map, 'seatId'),
        MapUtils.getOrNull<string>(map, 'ticketTemplateId'),
        MapUtils.getStringOrNull(map, 'actionBy'),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'actionDate')),
        toEnumFromString(MapUtils.getString(map, 'status'), SeatReservationItemStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'seatName': context.toData(this.seatName),
      'seatId': this.seatId,
      'ticketTemplateId': this.ticketTemplateId,
      'actionBy': this.actionBy,
      'actionDate': context.encodeDateOrNull(this.actionDate),
      'status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    seatName = this.seatName.clone(),
    seatId = this.seatId,
    ticketTemplateId = this.ticketTemplateId,
    actionBy = this.actionBy,
    actionDate = TimeZone.cloneDateOrNull(this.actionDate),
    status = this.status,
  }: {
    seatName?: LocalizedString;
    seatId?: number;
    ticketTemplateId?: string | null;
    actionBy?: string | null;
    actionDate?: dayjs.Dayjs | null;
    status?: SeatReservationItemStatus;
  } = {}): this {
    return new SeatReservationItem(
        seatName,
        seatId,
        ticketTemplateId,
        actionBy,
        actionDate,
        status,
    ) as this;
  }

  isSame(other: SeatReservationItem): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.seatName, this.seatName) &&
        other.seatId === this.seatId &&
        other.ticketTemplateId === this.ticketTemplateId &&
        other.actionBy == this.actionBy &&
        other.actionDate == this.actionDate &&
        other.status === this.status;
  }
}

export class SeatReservationItemBuilder extends DataModelBuilder<SeatReservationItem> {
  build(context: SerializationContext, map: SerializedData): SeatReservationItem {
    return SeatReservationItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(SeatReservationItem.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(SeatReservationItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following changes were made:
    // - parameter `ticketTemplateId` of type `string | null` was introduced
    if(version < 2) {
      version = 2;
    }

    // In this version the following changes were made:
    // - parameter `cancelledBy` of type `string | null` was introduced
    // - parameter `cancelledDate` of type `dayjs.Dayjs | null` was introduced
    if(version < 3) {
      version = 3;
    }

    // In this version the following changes were made:
    // - parameters `cancelledBy` and `cancelledDate` were renamed to `actionBy` and `actionDate`
    // - parameter `status` of type `SeatReservationItemStatus` was introduced
    if(version < 4) {
      const isCancelled = MapUtils.getIntegerOrNull(map, 'cancelledDate') != null;
      map['actionBy'] ??= MapUtils.getStringOrNull(map, 'cancelledBy');
      map['actionDate'] ??= MapUtils.getIntegerOrNull(map, 'cancelledDate');
      map['status'] ??= fromEnumAsString(isCancelled ? SeatReservationItemStatus.Cancelled : SeatReservationItemStatus.Live);
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: SeatReservationItem,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? SeatReservationItem.SCHEMA_VERSION;

    if(version >= 4) {
      version = 3;
      const isLive = model.status === SeatReservationItemStatus.Live;
      map['cancelledBy'] = isLive ? null : model.actionBy;
      map['cancelledDate'] = isLive ? null : context.encodeDateOrNull(model.actionDate);
      map['$schema_version'] = VersionRange.encodedBetween(3, 4);
    }

    return map;
  }
}
